import { useState, useEffect } from 'react'

export interface SourceToolDefinition {
  id: string
  label: string
  description: string
}

/** Fixed list of available sources/tools the user can enable per conversation. */
export const SOURCE_TOOL_DEFINITIONS: SourceToolDefinition[] = [
  {
    id: 'rag',
    label: 'RAG (búsqueda semántica)',
    description: 'Búsqueda sobre chunks de la organización',
  },
  {
    id: 'research',
    label: 'Research (Tavily/Perplexity)',
    description: 'Búsqueda externa para investigación',
  },
]

export interface ConversationSourceControlsProps {
  conversationId: string
  initialActiveSources?: string[]
  onSave?: (activeSources: string[]) => void | Promise<void>
}

export function ConversationSourceControls({
  conversationId,
  initialActiveSources = [],
  onSave,
}: ConversationSourceControlsProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const initial = initialActiveSources.length > 0
      ? initialActiveSources
      : SOURCE_TOOL_DEFINITIONS.map((d) => d.id)
    return new Set(initial)
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const initial =
      initialActiveSources.length > 0
        ? initialActiveSources
        : SOURCE_TOOL_DEFINITIONS.map((d) => d.id)
    setSelectedIds(new Set(initial))
  }, [conversationId, initialActiveSources.join(',')])

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    if (!onSave) return
    setSaving(true)
    try {
      await onSave(Array.from(selectedIds))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
      <p className="text-sm font-medium text-slate-700 mb-3">
        Fuentes y herramientas para esta conversación
      </p>
      <ul className="space-y-2 mb-4">
        {SOURCE_TOOL_DEFINITIONS.map((def) => (
          <li key={def.id} className="flex items-start gap-3">
            <input
              type="checkbox"
              id={`source-${conversationId}-${def.id}`}
              checked={selectedIds.has(def.id)}
              onChange={() => toggle(def.id)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label
              htmlFor={`source-${conversationId}-${def.id}`}
              className="text-sm text-slate-700 cursor-pointer"
            >
              <span className="font-medium">{def.label}</span>
              <span className="text-slate-500 block text-xs">{def.description}</span>
            </label>
          </li>
        ))}
      </ul>
      {onSave && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary text-sm"
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      )}
    </div>
  )
}
